import React, { useState, useMemo } from 'react';
import { Sale, PurchaseOrder, Expenditure, Product, Vendor } from '../types';
import { analyzeFinancialData } from '../services/geminiService';
import { 
  TrendingUp, TrendingDown, DollarSign, Download, Sparkles, 
  FileText, Calendar, Filter, PieChart as PieChartIcon, 
  ChevronRight, BarChart3, Receipt, Package, Truck, Wallet,
  Scale, Calculator, XCircle, AlertCircle, Box, Activity,
  Users, BarChart as BarChartIcon, ShoppingBag
} from 'lucide-react';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, 
  Legend, ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area 
} from 'recharts';

interface ReportsProps {
  sales: Sale[];
  purchases: PurchaseOrder[];
  expenditures: Expenditure[];
  products: Product[];
  vendors: Vendor[];
}

type ReportTab = 'SALES' | 'PURCHASES' | 'EXPENDITURES' | 'CASH' | 'INVENTORY' | 'BALANCE_SHEET' | 'INCOME_SHEET' | 'PROFIT_LOSS';

const COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

const Reports: React.FC<ReportsProps> = ({ sales, purchases, expenditures, products, vendors }) => {
  const [activeTab, setActiveTab] = useState<ReportTab>('SALES');
  const [timeframe, setTimeframe] = useState<'DAILY' | 'MONTHLY' | 'YEARLY'>('MONTHLY');
  const [aiAnalysis, setAiAnalysis] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const today = new Date().toISOString().split('T')[0];
  const currentMonth = today.slice(0, 7);
  const currentYear = today.slice(0, 4);

  // --- FILTERED DATA LOGIC ---
  const filteredSales = useMemo(() => {
    if (timeframe === 'DAILY') return sales.filter(s => s.date === today);
    if (timeframe === 'MONTHLY') return sales.filter(s => s.date.startsWith(currentMonth));
    return sales.filter(s => s.date.startsWith(currentYear));
  }, [sales, timeframe, today, currentMonth, currentYear]);

  const filteredPurchases = useMemo(() => {
    const received = purchases.filter(p => p.status === 'Received');
    if (timeframe === 'DAILY') return received.filter(p => p.date === today);
    if (timeframe === 'MONTHLY') return received.filter(p => p.date.startsWith(currentMonth));
    return received.filter(p => p.date.startsWith(currentYear));
  }, [purchases, timeframe, today, currentMonth, currentYear]);

  const filteredExpenditures = useMemo(() => {
    if (timeframe === 'DAILY') return expenditures.filter(e => e.date === today);
    if (timeframe === 'MONTHLY') return expenditures.filter(e => e.date.startsWith(currentMonth));
    return expenditures.filter(e => e.date.startsWith(currentYear));
  }, [expenditures, timeframe, today, currentMonth, currentYear]);

  // --- VENDOR & ORDER ANALYTICS ---
  const vendorPurchaseSummary = useMemo(() => {
    const summary: { [key: string]: { name: string, totalOrders: number, totalCost: number } } = {};
    
    filteredPurchases.forEach(p => {
      if (!summary[p.vendorId]) {
        summary[p.vendorId] = { name: p.vendorName, totalOrders: 0, totalCost: 0 };
      }
      summary[p.vendorId].totalOrders += 1;
      summary[p.vendorId].totalCost += p.totalCost;
    });

    return Object.values(summary).sort((a, b) => b.totalCost - a.totalCost);
  }, [filteredPurchases]);

  // --- DERIVED AGGREGATIONS ---
  const totalRevenue = filteredSales.reduce((acc, s) => acc + s.totalAmount, 0);
  const totalPurchaseCost = filteredPurchases.reduce((acc, p) => acc + p.totalCost, 0);
  const totalOperatingExpense = filteredExpenditures.reduce((acc, e) => acc + e.amount, 0);
  const totalOrdersCount = filteredPurchases.length;
  
  // Financial Estimations - calculate COGS from sale items
  const costOfGoodsSold = useMemo(() => {
    return filteredSales.reduce((totalCost, sale) => {
        return totalCost + sale.items.reduce((saleCost, item) => {
            // Find the original product to get its base cost
            const productDetails = products.find(p => p.id === item.id);
            if (!productDetails) return saleCost;
            
            // The cost in the sale_items table is per base unit
            let unitsSold = item.quantity;
            if (item.saleUnit === 'Pack') {
                unitsSold *= (item.stripsPerPack * item.unitsPerStrip);
            } else if (item.saleUnit === 'Strip') {
                unitsSold *= item.unitsPerStrip;
            }
            return saleCost + (unitsSold * productDetails.cost);
        }, 0);
    }, 0);
  }, [filteredSales, products]);


  const grossProfit = totalRevenue - costOfGoodsSold;
  const netIncome = grossProfit - totalOperatingExpense;
  const profitMargin = totalRevenue > 0 ? (netIncome / totalRevenue) * 100 : 0;

  // Inventory Valuations
  const inventoryValueAtCost = products.reduce((acc, p) => acc + (p.stock * p.cost), 0);
  const inventoryValueAtPrice = products.reduce((acc, p) => acc + (p.stock * p.priceUnit), 0);
  const potentialProfit = inventoryValueAtPrice - inventoryValueAtCost;

  const handleGenerateInsight = async () => {
    setLoading(true);
    const summary = {
      sales: totalRevenue,
      costOfGoods: costOfGoodsSold,
      procurement: totalPurchaseCost,
      overhead: totalOperatingExpense,
      inventoryValuation: inventoryValueAtCost,
      netProfit: netIncome,
      timeframe: timeframe,
      vendorPerformance: vendorPurchaseSummary
    };
    const analysis = await analyzeFinancialData(summary, timeframe);
    setAiAnalysis(analysis);
    setLoading(false);
  };

  const navItems = [
    { id: 'SALES', label: 'Sale Reports', icon: <TrendingUp size={18}/> },
    { id: 'PURCHASES', label: 'Purchase & Vendor Reports', icon: <Truck size={18}/> },
    { id: 'EXPENDITURES', label: 'Expenditure Reports', icon: <Receipt size={18}/> },
    { id: 'CASH', label: 'Cash Flow', icon: <Wallet size={18}/> },
    { id: 'INVENTORY', label: 'Inventory Reports', icon: <Package size={18}/> },
    { id: 'BALANCE_SHEET', label: 'Balance Sheet', icon: <Scale size={18}/> },
    { id: 'INCOME_SHEET', label: 'Income Sheet', icon: <Calculator size={18}/> },
    { id: 'PROFIT_LOSS', label: 'Profit & Loss', icon: <BarChart3 size={18}/> },
  ];

  return (
    <div className="flex flex-col xl:flex-row gap-8 min-h-screen pb-20">
      {/* Reports Sidebar Navigation */}
      <div className="w-full xl:w-72 space-y-2">
        <div className="mb-6 px-4">
          <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Reports Navigator</h3>
        </div>
        <div className="flex flex-wrap xl:flex-col gap-2">
          {navItems.map(item => (
            <button
              key={item.id}
              onClick={() => {
                setActiveTab(item.id as ReportTab);
                setAiAnalysis(null);
              }}
              className={`flex items-center gap-3 px-5 py-3.5 rounded-2xl font-bold transition-all ${
                activeTab === item.id 
                  ? 'bg-emerald-600 text-white shadow-xl shadow-emerald-600/20 translate-x-1' 
                  : 'bg-white text-slate-500 hover:bg-slate-50 border border-slate-100'
              }`}
            >
              {item.icon}
              <span className="text-sm">{item.label}</span>
              {activeTab === item.id && <ChevronRight size={16} className="ml-auto" />}
            </button>
          ))}
        </div>

        {/* AI Insight Section */}
        <div className="mt-8 bg-slate-900 rounded-3xl p-6 text-white overflow-hidden relative group">
          <div className="absolute -right-4 -top-4 opacity-10 group-hover:scale-110 transition-transform">
             <Sparkles size={100} />
          </div>
          <div className="relative z-10">
            <h4 className="font-bold mb-2 flex items-center gap-2">
              <Sparkles className="text-emerald-400" size={16} />
              AI Analyst
            </h4>
            <p className="text-xs text-slate-400 mb-4 leading-relaxed">Let Gemini analyze your {activeTab.toLowerCase().replace('& ', '').replace('reports', '')} for {timeframe.toLowerCase()} trends.</p>
            <button 
              onClick={handleGenerateInsight}
              disabled={loading}
              className="w-full py-2.5 bg-emerald-600 hover:bg-emerald-500 rounded-xl text-xs font-black transition-all disabled:opacity-50"
            >
              {loading ? 'Analyzing...' : 'Generate Insight'}
            </button>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 space-y-6">
        {/* Header with Timeframe Filter */}
        <div className="bg-white p-6 rounded-[2rem] shadow-sm border border-slate-200 flex flex-col md:flex-row justify-between items-center gap-4">
          <div>
            <h2 className="text-2xl font-black text-slate-900 uppercase tracking-tight">{navItems.find(n => n.id === activeTab)?.label}</h2>
            <p className="text-sm text-slate-500 font-medium">Performance Metrics: {timeframe} Review</p>
          </div>
          <div className="flex items-center bg-slate-100 p-1.5 rounded-2xl shadow-inner">
            {(['DAILY', 'MONTHLY', 'YEARLY'] as const).map(t => (
              <button
                key={t}
                onClick={() => setTimeframe(t)}
                className={`px-6 py-2 rounded-xl text-xs font-black transition-all ${
                  timeframe === t ? 'bg-white text-emerald-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                {t}
              </button>
            ))}
          </div>
        </div>

        {/* AI Analysis Result */}
        {aiAnalysis && (
          <div className="bg-emerald-50 border border-emerald-100 p-8 rounded-[2.5rem] relative animate-in slide-in-from-top-4 duration-500 shadow-sm">
            <button onClick={() => setAiAnalysis(null)} className="absolute top-6 right-6 text-emerald-300 hover:text-emerald-600">
              <XCircle size={24} />
            </button>
            <div className="flex items-start gap-4">
              <div className="p-3 bg-white rounded-2xl shadow-sm">
                <Sparkles className="text-emerald-600" size={24} />
              </div>
              <div className="flex-1">
                <h4 className="font-black text-emerald-900 mb-2 uppercase tracking-widest text-[10px]">Strategic Business Insight ({timeframe})</h4>
                <p className="text-emerald-800 leading-relaxed font-medium text-sm">{aiAnalysis}</p>
              </div>
            </div>
          </div>
        )}

        {/* Tab-Specific Content */}
        <div className="space-y-8">
          
          {/* SALES REPORT TAB */}
          {activeTab === 'SALES' && (
            <div className="space-y-6 animate-in fade-in duration-500">
               <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <ReportStatCard title="Total Revenue" value={`RS. ${totalRevenue.toLocaleString()}`} icon={<TrendingUp size={20}/>} color="text-emerald-600" bg="bg-emerald-50" />
                  <ReportStatCard title="Transactions" value={filteredSales.length.toString()} icon={<Receipt size={20}/>} color="text-blue-600" bg="bg-blue-50" />
                  <ReportStatCard title="Daily Sales Avg." value={`RS. ${(totalRevenue / (timeframe === 'YEARLY' ? 365 : timeframe === 'MONTHLY' ? 30 : 1)).toLocaleString(undefined, {maximumFractionDigits: 0})}`} icon={<Activity size={20}/>} color="text-amber-600" bg="bg-amber-50" />
               </div>
               <div className="bg-white p-8 rounded-[2.5rem] border border-slate-200 shadow-sm">
                  <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-6">Revenue Growth Trend ({timeframe})</h4>
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                       <AreaChart data={filteredSales.length ? filteredSales.map(s => ({ name: s.date.slice(-5), value: s.totalAmount })) : [{name: 'N/A', value: 0}]}>
                          <defs>
                            <linearGradient id="colorSales" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#10b981" stopOpacity={0.1}/>
                              <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                          <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{fontSize: 10, fill: '#94a3b8'}} />
                          <YAxis axisLine={false} tickLine={false} tick={{fontSize: 10, fill: '#94a3b8'}} />
                          <Tooltip contentStyle={{borderRadius: '16px', border: 'none', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)'}} />
                          <Area type="monotone" dataKey="value" stroke="#10b981" strokeWidth={3} fillOpacity={1} fill="url(#colorSales)" />
                       </AreaChart>
                    </ResponsiveContainer>
                  </div>
               </div>
            </div>
          )}

          {/* PURCHASE & VENDOR REPORT TAB */}
          {activeTab === 'PURCHASES' && (
            <div className="space-y-6 animate-in fade-in duration-500">
               {/* KPIs for Purchases */}
               <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <ReportStatCard title="Total Cost of Orders" value={`RS. ${totalPurchaseCost.toLocaleString()}`} icon={<Truck size={20}/>} color="text-red-600" bg="bg-red-50" />
                  <ReportStatCard title="Total Orders Count" value={totalOrdersCount.toString()} icon={<ShoppingBag size={20}/>} color="text-blue-600" bg="bg-blue-50" />
                  <ReportStatCard title="Suppliers Involved" value={new Set(filteredPurchases.map(p => p.vendorId)).size.toString()} icon={<Users size={20}/>} color="text-amber-600" bg="bg-amber-50" />
               </div>

               {/* Vendor Performance Report */}
               <div className="bg-white p-8 rounded-[2.5rem] border border-slate-200 shadow-sm">
                  <div className="flex justify-between items-center mb-6">
                    <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Supplier Purchase Summary ({timeframe})</h4>
                  </div>
                  <div className="overflow-x-auto rounded-2xl border border-slate-50">
                    <table className="w-full text-left">
                      <thead className="bg-slate-50 border-b border-slate-100 text-[10px] font-black uppercase tracking-widest text-slate-400">
                        <tr>
                          <th className="px-6 py-4">Vendor / Supplier Name</th>
                          <th className="px-6 py-4 text-center">Total Orders</th>
                          <th className="px-6 py-4 text-right">Total Purchase Cost</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-50">
                        {vendorPurchaseSummary.map((v, idx) => (
                          <tr key={idx} className="hover:bg-slate-50 transition-colors group">
                            <td className="px-6 py-4 flex items-center gap-3 font-bold text-slate-900">
                               <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-[10px] text-slate-400 group-hover:bg-emerald-100 group-hover:text-emerald-600 transition-colors">
                                  {v.name.charAt(0)}
                               </div>
                               {v.name}
                            </td>
                            <td className="px-6 py-4 text-center font-medium text-slate-600">
                               <span className="px-3 py-1 bg-blue-50 text-blue-600 rounded-full text-xs font-bold">{v.totalOrders} POs</span>
                            </td>
                            <td className="px-6 py-4 text-right font-black text-red-600">
                               RS. {v.totalCost.toLocaleString()}
                            </td>
                          </tr>
                        ))}
                        {vendorPurchaseSummary.length === 0 && (
                          <tr><td colSpan={3} className="p-20 text-center text-slate-400 italic">No purchase records found for this timeframe.</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
               </div>

               {/* Purchase Order Cost Analysis (Graph) */}
               <div className="bg-white p-8 rounded-[2.5rem] border border-slate-200 shadow-sm">
                  <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-6">Procurement Cost Trend ({timeframe})</h4>
                  <div className="h-64">
                     <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={filteredPurchases.length ? filteredPurchases.map(p => ({ name: p.date.slice(-5), cost: p.totalCost })) : [{name: 'N/A', cost: 0}]}>
                           <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                           <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{fontSize: 10}} />
                           <YAxis axisLine={false} tickLine={false} tick={{fontSize: 10}} />
                           <Tooltip cursor={{fill: '#f8fafc'}} contentStyle={{borderRadius: '12px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'}} />
                           <Bar dataKey="cost" fill="#ef4444" radius={[6, 6, 0, 0]} barSize={20} />
                        </BarChart>
                     </ResponsiveContainer>
                  </div>
               </div>
            </div>
          )}

          {/* EXPENDITURE REPORT TAB */}
          {activeTab === 'EXPENDITURES' && (
            <div className="space-y-6 animate-in fade-in duration-500">
               <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <ReportStatCard title="Total Operating Exp." value={`RS. ${totalOperatingExpense.toLocaleString()}`} icon={<Receipt size={20}/>} color="text-red-600" bg="bg-red-50" />
                  <ReportStatCard title="Active Categories" value={new Set(filteredExpenditures.map(e => e.category)).size.toString()} icon={<Filter size={20}/>} color="text-slate-600" bg="bg-slate-50" />
               </div>
               <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <div className="bg-white p-8 rounded-[2.5rem] border border-slate-200 shadow-sm">
                     <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-6">Expenditure Category Mix</h4>
                     <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                           <PieChart>
                              <Pie 
                                 data={Object.entries(filteredExpenditures.reduce((acc, e) => ({...acc, [e.category]: (acc[e.category] || 0) + e.amount}), {} as any)).map(([k, v]) => ({name: k, value: v}))}
                                 innerRadius={60}
                                 outerRadius={80}
                                 paddingAngle={5}
                                 dataKey="value"
                              >
                                 {COLORS.map((c, i) => <Cell key={i} fill={c} />)}
                              </Pie>
                              <Tooltip />
                              <Legend />
                           </PieChart>
                        </ResponsiveContainer>
                     </div>
                  </div>
                  <div className="bg-white p-6 rounded-[2.5rem] border border-slate-200 overflow-y-auto max-h-[400px] shadow-sm">
                     <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4 px-2">Recent Expense Ledger</h4>
                     <div className="space-y-2">
                        {filteredExpenditures.map(e => (
                           <div key={e.id} className="flex justify-between items-center p-4 bg-slate-50 rounded-2xl hover:bg-slate-100 transition-colors">
                              <div>
                                 <p className="text-sm font-bold text-slate-900">{e.description}</p>
                                 <p className="text-[10px] font-bold text-slate-400 uppercase">{e.category} • {e.date}</p>
                              </div>
                              <span className="font-black text-red-600 text-sm">RS. {e.amount.toLocaleString()}</span>
                           </div>
                        ))}
                        {filteredExpenditures.length === 0 && <p className="p-10 text-center text-slate-400 italic">No expenses recorded for this period.</p>}
                     </div>
                  </div>
               </div>
            </div>
          )}

          {/* CASH FLOW TAB */}
          {activeTab === 'CASH' && (
            <div className="space-y-6 animate-in fade-in duration-500">
               <div className="bg-white p-12 rounded-[3.5rem] border border-slate-200 shadow-sm">
                  <div className="flex flex-col sm:flex-row justify-between items-start mb-12 gap-6">
                    <div>
                      <h3 className="text-2xl font-black text-slate-900 uppercase">Cash Flow Analysis</h3>
                      <p className="text-sm text-slate-500 mt-1">Comparison of liquid capital inflow and outflow</p>
                    </div>
                    <div className="flex items-center gap-6 bg-slate-50 p-4 rounded-3xl border border-slate-100">
                       <div className="flex items-center gap-2 text-[10px] font-black uppercase text-emerald-600">
                          <div className="w-3 h-3 bg-emerald-500 rounded-full animate-pulse"></div> Sales Inflow
                       </div>
                       <div className="flex items-center gap-2 text-[10px] font-black uppercase text-red-600">
                          <div className="w-3 h-3 bg-red-500 rounded-full"></div> Operational Outflow
                       </div>
                    </div>
                  </div>
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={[{name: 'Cash In (Sales)', val: totalRevenue}, {name: 'Cash Out (Exp + PO)', val: totalPurchaseCost + totalOperatingExpense}]}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                        <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{fontSize: 10, fontWeight: 'bold'}} />
                        <YAxis axisLine={false} tickLine={false} tick={{fontSize: 10}} />
                        <Tooltip cursor={{fill: 'transparent'}} />
                        <Bar dataKey="val" radius={[15, 15, 0, 0]}>
                           <Cell fill="#10b981" />
                           <Cell fill="#ef4444" />
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="mt-12 pt-10 border-t border-slate-100 grid grid-cols-1 md:grid-cols-3 gap-8">
                      <div className="text-center">
                         <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Inflow Volume</p>
                         <p className="text-2xl font-black text-emerald-600">RS. {totalRevenue.toLocaleString()}</p>
                      </div>
                      <div className="text-center">
                         <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Outflow Volume</p>
                         <p className="text-2xl font-black text-red-600">RS. {(totalPurchaseCost + totalOperatingExpense).toLocaleString()}</p>
                      </div>
                      <div className="text-center bg-slate-900 p-6 rounded-3xl text-white">
                         <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Net Cash Delta</p>
                         <p className="text-2xl font-black">RS. {(totalRevenue - (totalPurchaseCost + totalOperatingExpense)).toLocaleString()}</p>
                      </div>
                  </div>
               </div>
            </div>
          )}

          {/* INVENTORY REPORT TAB */}
          {activeTab === 'INVENTORY' && (
             <div className="space-y-6 animate-in fade-in duration-500">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                   <ReportStatCard title="Inventory Valuation (Cost)" value={`RS. ${inventoryValueAtCost.toLocaleString()}`} icon={<Package size={20}/>} color="text-slate-900" bg="bg-slate-100" />
                   <ReportStatCard title="Potential Market Value" value={`RS. ${inventoryValueAtPrice.toLocaleString()}`} icon={<TrendingUp size={20}/>} color="text-emerald-600" bg="bg-emerald-50" />
                   <ReportStatCard title="Expected Margin" value={`RS. ${potentialProfit.toLocaleString()}`} icon={<DollarSign size={20}/>} color="text-blue-600" bg="bg-blue-50" />
                </div>
                <div className="bg-white p-10 rounded-[2.5rem] border border-slate-200 shadow-sm">
                   <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-8">Asset Concentration by Category</h4>
                   <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-6">
                      {Object.entries(products.reduce((acc, p) => ({...acc, [p.category]: (acc[p.category] || 0) + (p.stock * p.cost)}), {} as any)).map(([cat, value]) => (
                        <div key={cat} className="p-5 bg-slate-50 rounded-[2rem] border border-slate-100 hover:border-emerald-200 hover:bg-white transition-all group">
                           <p className="text-[9px] font-black text-slate-400 uppercase tracking-tighter mb-1 truncate">{cat}</p>
                           <p className="text-sm font-black text-slate-900 truncate">RS. {Number(value).toLocaleString()}</p>
                           <div className="h-1 w-0 bg-emerald-500 mt-2 rounded-full group-hover:w-full transition-all duration-500"></div>
                        </div>
                      ))}
                   </div>
                </div>
             </div>
          )}

          {/* BALANCE SHEET TAB */}
          {activeTab === 'BALANCE_SHEET' && (
            <div className="bg-white p-16 rounded-[4rem] border border-slate-200 max-w-4xl mx-auto shadow-2xl animate-in fade-in duration-500">
               <div className="text-center mb-20 border-b border-slate-100 pb-12">
                  <h2 className="text-4xl font-black text-slate-900 tracking-tighter uppercase">Balance Sheet</h2>
                  <p className="text-slate-400 font-bold uppercase tracking-[0.3em] mt-3 text-xs">As of {new Date().toLocaleDateString()} • Abdullah Medical Store</p>
               </div>

               <div className="space-y-16">
                  {/* ASSETS SECTION */}
                  <section>
                    <div className="flex items-center gap-4 mb-8">
                       <div className="h-10 w-10 rounded-2xl bg-emerald-600 text-white flex items-center justify-center shadow-lg shadow-emerald-600/20">
                          <Activity size={20} />
                       </div>
                       <h3 className="text-sm font-black text-slate-900 uppercase tracking-widest">Current & Fixed Assets</h3>
                    </div>
                    <div className="space-y-4 px-4">
                       <LedgerRow label="Liquid Cash in Hand (Projected)" amount={totalRevenue * 0.35} />
                       <LedgerRow label="Drug Inventory (Current Valuation)" amount={inventoryValueAtCost} />
                       <LedgerRow label="Accounts Receivable" amount={totalRevenue * 0.05} />
                       <LedgerRow label="Prepaid Shop Overheads" amount={totalOperatingExpense * 0.2} />
                       <div className="pt-6 border-t border-slate-100 flex justify-between font-black text-slate-900 bg-emerald-50/40 p-6 rounded-3xl mt-6">
                          <span className="uppercase text-[11px] tracking-widest">Total Tangible Assets</span>
                          <span className="text-xl">RS. {( (totalRevenue * 0.35) + inventoryValueAtCost + (totalRevenue * 0.05) + (totalOperatingExpense * 0.2) ).toLocaleString()}</span>
                       </div>
                    </div>
                  </section>

                  {/* LIABILITIES SECTION */}
                  <section>
                    <div className="flex items-center gap-4 mb-8">
                       <div className="h-10 w-10 rounded-2xl bg-red-600 text-white flex items-center justify-center shadow-lg shadow-red-600/20">
                          <Scale size={20} />
                       </div>
                       <h3 className="text-sm font-black text-slate-900 uppercase tracking-widest">Liabilities & Owner's Equity</h3>
                    </div>
                    <div className="space-y-4 px-4">
                       <LedgerRow label="Outstanding Supplier Payables (Vendor AP)" amount={vendors.reduce((acc, v) => acc + v.payableAmount, 0)} />
                       <LedgerRow label="Accrued Salary & Utilities Payables" amount={totalOperatingExpense * 0.12} />
                       <LedgerRow label="Long-term Business Obligations" amount={0} />
                       <LedgerRow label="Owner's Invested Capital & Earnings" amount={500000 + netIncome} />
                       <div className="pt-6 border-t border-slate-100 flex justify-between font-black text-slate-900 bg-slate-900 text-white p-6 rounded-3xl mt-6">
                          <span className="uppercase text-[11px] tracking-widest">Total Liabilities & Equity</span>
                          <span className="text-xl">RS. {(vendors.reduce((acc,v)=>acc+v.payableAmount,0) + (totalOperatingExpense*0.12) + 500000 + netIncome).toLocaleString()}</span>
                       </div>
                    </div>
                  </section>
               </div>
               <div className="mt-20 pt-10 border-t border-dashed border-slate-200 text-center italic text-slate-300 text-[10px] uppercase font-bold tracking-widest">
                  Internal Accounting Document • Not for Statutory Audit
               </div>
            </div>
          )}

          {/* INCOME SHEET TAB */}
          {activeTab === 'INCOME_SHEET' && (
            <div className="bg-white p-12 rounded-[3rem] border border-slate-200 max-w-4xl mx-auto shadow-sm animate-in fade-in duration-500">
               <h2 className="text-center text-3xl font-black text-slate-900 mb-16 tracking-tighter uppercase underline decoration-emerald-500 decoration-8 underline-offset-8">Income Statement</h2>
               <div className="space-y-12">
                  <div className="bg-slate-50 p-10 rounded-[3rem] border border-slate-100 shadow-inner">
                    <PLItem label="Total Gross Inflow (Sales)" value={totalRevenue} color="text-emerald-700" bold />
                    <PLItem label="Direct COGS (Cost of Goods Sold)" value={costOfGoodsSold} color="text-red-500" indent />
                    <div className="mt-6 pt-6 border-t-2 border-slate-200">
                       <PLItem label="Gross Operating Margin" value={grossProfit} color="text-slate-900" bold bg="bg-white px-6 py-3 rounded-2xl border border-slate-100" />
                    </div>
                  </div>

                  <div className="px-10 space-y-5">
                     <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-6">Indirect Expenditure Adjustments</h4>
                     <PLItem label="General Administrative Overheads" value={totalOperatingExpense} color="text-red-500" />
                     
                     <div className="mt-12 pt-10 border-t-4 border-slate-900">
                        <div className="flex justify-between items-center bg-[#020617] text-white p-10 rounded-[3rem] shadow-2xl relative overflow-hidden group">
                           <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/10 rounded-full blur-3xl group-hover:scale-150 transition-transform"></div>
                           <div className="relative z-10">
                              <p className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-400">Consolidated Net Profit</p>
                              <p className="text-5xl font-black mt-2 tracking-tighter">RS. {netIncome.toLocaleString()}</p>
                           </div>
                           <div className={`relative z-10 px-6 py-3 rounded-2xl font-black text-xs tracking-widest border-2 ${netIncome >= 0 ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : 'bg-red-500/10 text-red-400 border-red-500/20'}`}>
                              {profitMargin.toFixed(2)}% MARGIN
                           </div>
                        </div>
                     </div>
                  </div>
               </div>
            </div>
          )}

          {/* PROFIT & LOSS TAB */}
          {activeTab === 'PROFIT_LOSS' && (
            <div className="space-y-8 animate-in fade-in duration-500">
               <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                  <div className="bg-white p-12 rounded-[3.5rem] border border-slate-200 shadow-sm relative overflow-hidden">
                     <div className="absolute top-0 right-0 p-8 text-slate-100 -mr-10 -mt-10">
                        <BarChartIcon size={120} />
                     </div>
                     <h3 className="text-2xl font-black text-slate-900 mb-10 pb-6 border-b border-slate-100 flex items-center gap-4 relative z-10">
                        <Activity className="text-emerald-500" /> P&L Breakdown
                     </h3>
                     <div className="space-y-7 relative z-10">
                        <PLItem label="Inflow (Revenue Volume)" value={totalRevenue} color="text-emerald-600" bold />
                        <PLItem label="Cost of Goods Sold" value={costOfGoodsSold} color="text-red-500" indent />
                        <div className="border-t border-slate-100 pt-6">
                           <PLItem label="Operational Gross Profit" value={grossProfit} color="text-slate-900" bold bg="bg-slate-50 px-6 py-3 rounded-2xl" />
                        </div>
                        <PLItem label="Fixed Operating Overheads" value={totalOperatingExpense} color="text-red-500" indent />
                        <div className="border-t-4 border-slate-900 pt-10 mt-10">
                           <PLItem label="Final Net Profit" value={netIncome} color={netIncome >= 0 ? 'text-emerald-700' : 'text-red-700'} bold textLarge />
                        </div>
                     </div>
                  </div>
                  <div className="flex flex-col gap-10">
                     <div className="flex-1 bg-white p-10 rounded-[3rem] border border-slate-200 flex flex-col items-center justify-center shadow-sm">
                        <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-8">Business Health Gauge</h4>
                        <div className="relative h-56 w-56 rounded-full border-[15px] border-slate-50 flex items-center justify-center shadow-inner">
                           <div className="absolute inset-[-15px] rounded-full border-[15px] border-emerald-500 border-t-transparent border-l-transparent transition-all duration-1000" style={{transform: `rotate(${Math.min(100, Math.max(0, profitMargin)) * 3.6}deg)`}}></div>
                           <div className="text-center">
                              <p className="text-4xl font-black text-emerald-600">{profitMargin.toFixed(1)}%</p>
                              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">Net Yield</p>
                           </div>
                        </div>
                     </div>
                     <div className="bg-[#020617] p-12 rounded-[3rem] text-white shadow-2xl relative overflow-hidden group">
                        <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-emerald-500/10 rounded-full blur-2xl group-hover:scale-150 transition-all duration-700"></div>
                        <h4 className="text-[10px] font-black text-emerald-400 uppercase tracking-widest mb-3 relative z-10">Insight verdict</h4>
                        <p className="text-3xl font-black leading-tight relative z-10">Operationally {netIncome > 0 ? 'Stable & Profitable' : 'Negative Net Delta'}.</p>
                        <p className="text-xs text-slate-400 mt-6 leading-relaxed relative z-10 font-medium italic">
                           Analysis considers {totalOrdersCount} purchase orders and {filteredSales.length} sale transactions for the {timeframe.toLowerCase()} window.
                        </p>
                     </div>
                  </div>
               </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// --- SUB-COMPONENTS ---

const ReportStatCard = ({ title, value, icon, color, bg }: any) => (
  <div className={`p-8 rounded-[2.5rem] bg-white border border-slate-100 shadow-sm flex items-center gap-6 group hover:border-emerald-200 hover:shadow-xl transition-all`}>
    <div className={`p-5 rounded-2xl ${bg} ${color} transition-transform group-hover:scale-110 shadow-inner`}>
      {icon}
    </div>
    <div>
      <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">{title}</p>
      <p className={`text-2xl font-black ${color}`}>{value}</p>
    </div>
  </div>
);

const PLItem = ({ label, value, color, indent, bold, textLarge, bg }: any) => (
  <div className={`flex justify-between items-center ${indent ? 'pl-6' : ''} ${bg || ''}`}>
    <span className={`text-sm ${bold ? 'font-black text-slate-800' : 'text-slate-500'}`}>{label}</span>
    <span className={`${textLarge ? 'text-2xl' : 'text-sm'} font-black ${color}`}>
       {value < 0 ? `(RS. ${Math.abs(value).toLocaleString()})` : `RS. ${value.toLocaleString()}`}
    </span>
  </div>
);

const LedgerRow = ({ label, amount }: any) => (
  <div className="flex justify-between items-center text-sm group py-2">
     <span className="text-slate-500 group-hover:text-slate-900 transition-colors font-semibold">{label}</span>
     <span className="font-bold text-slate-900">RS. {amount.toLocaleString()}</span>
  </div>
);

export default Reports;